import isEmpty from 'es-toolkit/compat/isEmpty';
import { isEmptyObject } from 'es-toolkit';
import { type ComponentTFunction, type TFunction } from '@pkgs/components/providers/AppContextProvider';
import {
  type BigDecimalRangeDto,
  Currency,
  isRangeDto,
  type Language,
  type BusinessArea,
  type PropertyMarketingType,
  type SearchFilterRequestPropertyTypeSubType,
  type SearchListingsFilters,
  type SearchListingsOptions,
  type SurfaceFilterKey,
  UNIT_SYMBOL_MAP,
} from '@ev-tech/search-modules-api';

import { getFeatureKey } from './translationKeyUtils';
import { formatNumber, formatPriceWithoutDecimals } from './numberFormatters';
import { formatDisplayRange } from './filterLabelUtils';
import { getObjectTypeCategoryLabel, getPropertySubTypeLabel, getPropertyTypeLabel } from './aiSearchPropertyTypeUtils';
import { getSelectedPreferenceEntries } from './aiSearchPreferenceUtils';
import { type FilterRefinement } from '../types/types';
import { SearchAlertFrequency, SearchAlertFrequency as SearchAlertFrequencyV2 } from '../hooks/useSearchAlert';
import { type UIFacingPreferences } from '../hooks/stores/search/slices/preferencesSlice';
import { DEFAULT_PROPERTY_MARKETING_TYPE } from '../hooks/stores/search/slices/objectTypeFilterSlice';

export type FrequencyInSeconds = 0 | 86_400 | 604_800 | undefined;

export function getSecondsByFrequency(frequency: SearchAlertFrequency): FrequencyInSeconds {
  switch (frequency) {
    case SearchAlertFrequency.DAILY:
      return 86_400;
    case SearchAlertFrequency.WEEKLY:
      return 604_800;
    case SearchAlertFrequency.INSTANTLY:
      return 0;
    case SearchAlertFrequencyV2.NEVER:
      return undefined;
    default:
      return 0;
  }
}

function formatRangeValue(rangeValue: number, language: Language, currency?: Currency, isYear?: boolean, isCurrency?: boolean) {
  if (isYear) {
    return rangeValue;
  }
  return isCurrency
    ? formatPriceWithoutDecimals(rangeValue, language, currency ?? Currency.EUR)
    : formatNumber({ number: rangeValue, language }, { useGrouping: true });
}

function getRangeLabel(params: {
  t: ComponentTFunction;
  language: Language;
  range?: BigDecimalRangeDto;
  isCurrency?: boolean;
  isYear?: boolean;
  currency?: Currency;
}): string {
  const { t, language, range, isCurrency, isYear, currency } = params;
  if (!range?.min && !range?.max) {
    return '';
  }

  return formatDisplayRange({
    range: {
      min: range.min ? formatRangeValue(range.min, language, currency, isYear, isCurrency) : undefined,
      max: range.max ? formatRangeValue(range.max, language, currency, isYear, isCurrency) : undefined,
    },
    minimumLabel: t('search.searchbar.minLabel'),
    maximumLabel: t('search.searchbar.maxLabel'),
  });
}

export function getFilterDescription(
  filters: SearchListingsFilters,
  placeName: string,
  t: TFunction,
  options: SearchListingsOptions,
): { filters: string[]; aiLocationInterests?: string[] } {
  const typedTFunction = t as ComponentTFunction;

  const { propertyMarketingType, propertyTypeSubType, businessArea, price, placeIds, livingSurface, rooms, preferences, ...rest } = filters;

  const { language, currency } = options;

  const marketingType = propertyMarketingType[0] ?? DEFAULT_PROPERTY_MARKETING_TYPE;
  const marketingLabel = marketingType === 'rent' ? typedTFunction('search.searchbar.rent') : typedTFunction('search.searchbar.sale');

  // TODO: adjust the propertyType to be an array of values
  // check with PMs how to create the description string here
  const propertyLabels = getPropertyLabel(t, propertyTypeSubType, businessArea[0]);

  const livingAreaLabel = getSurfaceLabel({
    surfaceValue: livingSurface,
    type: 'livingSurface',
    t,
    options,
  });
  const priceLabel = getPriceLabel({
    price,
    propertyMarketingType: propertyMarketingType[0] ?? DEFAULT_PROPERTY_MARKETING_TYPE,
    t,
    currency,
    language,
  });
  const roomsLabel = getRoomsLabel(filters, language, t);
  const restFilterAttributes = mapFilterAttributesToString({
    filters: rest,
    t,
    options,
  });
  const preferenceLabels = getPreferenceLabels(preferences, t);

  const description = [marketingLabel, ...propertyLabels, placeName, livingAreaLabel, roomsLabel, priceLabel, ...restFilterAttributes];

  return { filters: description.filter(Boolean), aiLocationInterests: preferenceLabels };
}

export function getPreferenceLabels(preferences: UIFacingPreferences | undefined, t: ComponentTFunction): string[] {
  return getSelectedPreferenceEntries(preferences, t).map(({ label }) => label);
}

export function mapFilterAttributesToString(params: {
  filters: FilterRefinement;
  t: ComponentTFunction;
  options: SearchListingsOptions;
}): string[] {
  const { filters, t, options } = params;
  const { currency, language } = options;

  const attributesWithValues = Object.entries(filters).filter(([, value]) => {
    return value && (Array.isArray(value) ? !isEmpty(value) : !isEmptyObject(value));
  });

  const featureLabels = attributesWithValues.reduce<string[]>((labels, [key, value]) => {
    if (value === true && key !== 'searchAlert') {
      const featureKey = getFeatureKey(key);
      const featureLabel = featureKey ? t(featureKey) : '';

      if (featureLabel) {
        labels.push(featureLabel);
      }
    }
    return labels;
  }, []);

  const attributesWithRange = attributesWithValues.reduce<string[]>((result, [key, value]) => {
    if (isRangeDto(value)) {
      const attribute = key.toLowerCase().includes('surface')
        ? getSurfaceLabel({
            surfaceValue: value,
            type: key as SurfaceFilterKey,
            t,
            options,
          })
        : getAttributeRangeLabel({
            key: key as keyof SearchListingsFilters,
            range: value,
            t,
            currency,
            language,
          });

      result.push(attribute);
    }
    return result;
  }, []);

  return [...attributesWithRange, ...featureLabels];
}

export function getRoomsLabel(filters: FilterRefinement, language: Language, t: ComponentTFunction): string {
  const rangeLabel = getRangeLabel({
    t,
    language,
    range: filters.rooms,
  });

  return rangeLabel ? `${rangeLabel} ${t('search.searchbar.rooms')}` : '';
}

export function getPriceLabel(params: {
  price?: BigDecimalRangeDto;
  propertyMarketingType?: PropertyMarketingType;
  t: ComponentTFunction;
  currency: Currency;
  language: Language;
}): string {
  const { price, propertyMarketingType, t, currency, language } = params;

  if (!price || !propertyMarketingType) return '';

  const rangeLabel = getRangeLabel({
    t,
    language,
    range: price,
    isCurrency: true,
    isYear: false,
    currency,
  });
  const saleRentLabel = propertyMarketingType === 'rent' ? t('search.searchbar.rent') : t('search.searchbar.price');

  return rangeLabel ? `${saleRentLabel} ${rangeLabel}` : '';
}

export function getPropertyLabel(
  t: ComponentTFunction,
  propertyTypeSubType?: SearchFilterRequestPropertyTypeSubType,
  businessArea?: BusinessArea,
): string[] {
  const propertyTypeSubTypes = Object.entries(propertyTypeSubType ?? {});

  if (propertyTypeSubTypes.length === 0) {
    return businessArea ? [getObjectTypeCategoryLabel(businessArea, t)] : [];
  }

  return propertyTypeSubTypes.map(([type, subTypes]) => {
    const propertyTypeLabel = getPropertyTypeLabel(type as keyof SearchFilterRequestPropertyTypeSubType, t);
    if (!subTypes.length) {
      return propertyTypeLabel;
    } else {
      const propertySubTypeLabels = subTypes.map((subType) => getPropertySubTypeLabel(subType, t)).join(', ');
      return `${propertyTypeLabel} (${propertySubTypeLabels})`;
    }
  });
}

export function getSurfaceLabel(params: {
  surfaceValue?: BigDecimalRangeDto;
  type: SurfaceFilterKey;
  t: ComponentTFunction;
  options: SearchListingsOptions;
}): string {
  const {
    surfaceValue,
    type,
    t,
    options: { language, measurementSystem, currency },
  } = params;
  if (!surfaceValue) return '';

  let label = '';

  switch (type) {
    case 'livingSurface':
      label = t('search.searchbar.livingArea');
      break;
    case 'plotSurface':
      label = t('search.searchbar.advFilter.plotSurface');
      break;
    case 'totalSurface':
      label = t('search.searchbar.totalSurface');
      break;
  }
  const rangeLabel = getRangeLabel({
    t,
    language,
    range: surfaceValue,
    isCurrency: false,
    isYear: false,
    currency,
  });
  const unitSymbol = UNIT_SYMBOL_MAP[measurementSystem];

  return rangeLabel ? `${label} ${rangeLabel} ${unitSymbol}` : '';
}

type AttributeRangeTranslationKey =
  | 'search.searchbar.advFilter.bathrooms'
  | 'search.searchbar.advFilter.bedrooms'
  | 'search.searchbar.advFilter.constructionYear'
  | 'search.searchbar.advFilter.plotSurface'
  | 'search.searchbar.rooms'
  | 'search.searchbar.totalSurface';

const getAttributeRangeTranslationKey = (key: keyof SearchListingsFilters): AttributeRangeTranslationKey | '' => {
  switch (key) {
    case 'bathrooms':
      return 'search.searchbar.advFilter.bathrooms';
    case 'bedrooms':
      return 'search.searchbar.advFilter.bedrooms';
    case 'constructionYear':
      return 'search.searchbar.advFilter.constructionYear';
    case 'plotSurface':
      return 'search.searchbar.advFilter.plotSurface';
    case 'rooms':
      return 'search.searchbar.rooms';
    case 'totalSurface':
      return 'search.searchbar.totalSurface';
    default:
      return '';
  }
};

function getAttributeRangeLabel(params: {
  key: keyof SearchListingsFilters;
  range: BigDecimalRangeDto;
  t: ComponentTFunction;
  language: Language;
  currency: Currency;
}): string {
  const { key, range, t, language, currency } = params;
  const attributeKey = getAttributeRangeTranslationKey(key);
  const attributeLabel = attributeKey ? t(attributeKey) : '';
  const rangeLabel = getRangeLabel({
    t,
    language,
    range,
    isCurrency: false,
    isYear: key === 'constructionYear',
    currency,
  });
  return `${attributeLabel} ${rangeLabel}`;
}
